/**
 * What a LunaPin tab is made of, and how it is stored.
 *
 * The whole of LunaPin's contribution to the lunula shell's window model. The
 * toolkit owns tabs, windows, the sidebar, focus, dragging, resizing and
 * reordering; LunaPin owns exactly one question — *what can a window contain* —
 * and this file is the answer. Three cases today — [WindowContent.BookmarkList],
 * [WindowContent.Settings] and [WindowContent.EditBookmark] — and adding a fourth is a
 * case here plus a branch in the shell's content factory and one in its labeller,
 * exactly as the two after the first were.
 *
 * LPN-1's greeting was the first second case, for an afternoon, and it is deleted —
 * a window saying "Hello world" and who you are signed in as was scaffolding for
 * proving the wiring, and scaffolding that survives into a product becomes something
 * to read past. What it proved is proved by the list, doing the same round trip for
 * real data. The useful part of its short life is that the forward-compatibility
 * promise below stopped being a hope: blobs written under one case decoded fine under
 * two, and back again.
 *
 * ── The prediction, collected ────────────────────────────────────────────────
 *
 * This header used to say that when the list "grows an argument — a tag it is filtered
 * to, a search it is showing — this becomes a `data class` and the stored blobs keep
 * loading, since a missing field takes its default." LPN-16 is where that happened, and
 * it grew ten fields rather than one: a query, two tag sets, a match rule, a sort, a
 * grouping, an age window, a column count, a row density and an unread switch.
 *
 * **Every one of them defaults**, which is the whole of what made it safe — and it is now
 * tested rather than promised. `WorkspaceTest` holds a real blob written before any of
 * those fields existed and asserts it decodes to a list window with each of them at its
 * default, beside the two older blobs kept for the same reason. Predicting a thing is not
 * the same as pinning it; the pin is the part that survives somebody deciding a field
 * should be required.
 *
 * The other half of LPN-16 is [WorkspaceWindow.name], and it is a different kind of
 * change: not a new case, but a fact about a window that had nowhere to live because
 * nothing could be renamed. Its `null` is load-bearing — see the property.
 *
 * ── One menu row is not one kind ─────────────────────────────────────────────
 *
 * The toolbar's "+" offers exactly one thing, "New window", and it makes a list. That
 * is not because a list is all there is — it is because the other kinds are not things
 * you create from a menu. A settings window is opened by the gear; an edit window is
 * opened by the bookmark it edits. Neither is a blank one you would ask for and then
 * have to fill.
 *
 * So the kind travels as a parameter through [WorkspaceBackingViewModel.onWindowAdded]
 * rather than being implied by the one caller. That parameter was written before there
 * was a second kind to pass to it, and LPN-9 is where it stopped being speculative: the
 * gear passes [WindowContent.Settings], the "+" passes [WindowContent.BookmarkList], and
 * neither call site can be read without saying which window it makes.
 *
 * The `kind` discriminator this sealed interface forces into the stored blob is what
 * lets any of that happen without the workspaces people already have becoming
 * unreadable.
 *
 * ── Why window ids are stored rather than derived ────────────────────────────
 *
 * Lunicle derives its pane ids from what the pane shows (`board-7`, `issue-402`),
 * which is total in both directions and needs no lookup table. That works because
 * every Lunicle pane has an argument. A LunaPin "New window" has none, so two of
 * them in one tab would derive the same id and the second would silently replace
 * the first — and "open two windows side by side" is the thing the ticket is
 * asking for. So the id is a stored field, allocated by [Workspace.nextWindowId],
 * and the toolkit's own persisted geometry (which is keyed by window id) lines up
 * across reloads because the id round-trips through the workspace blob.
 *
 * LPN-16 gave the list an argument — ten of them — and that does **not** rescue the
 * derived-id idea, which is worth stating so nobody tries it. Two windows on the same
 * filter are still two ordinary windows, reached by pressing "+" twice, and they would
 * still derive one id; and a filter that changes as somebody types would be an id that
 * changes underneath the toolkit's geometry map. An id is stable exactly because it says
 * nothing about what the window contains.
 *
 * [WindowContent.EditBookmark] is the first kind that *does* have an argument, and it
 * does not weaken that: a workspace holding one list and one editor still needs an
 * allocator for the list, so deriving ids from content would have to be a rule with an
 * exception in it. One rule, applied to every window, is cheaper than two.
 *
 * @see WorkspaceBackingViewModel
 */
package se.soderbjorn.lunapin.client.viewmodel

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.Json

/**
 * What one window holds.
 *
 * A sealed interface, whose `kind` discriminator in the stored blob is what let LPN-9
 * add a second case without the workspaces people already had becoming unreadable — the
 * promise the single-case version was written to keep, now collected. Written as
 * nullable-payload-free ordinary Kotlin because nothing on the Apple side reads a
 * `WindowContent` yet — when something does, the architecture note's
 * one-non-null-field style applies (see §06 of docs/kmp-sharing-architecture.html).
 */
@Serializable
sealed interface WindowContent {
    /**
     * **A window is a saved filter**, and this is the filter.
     *
     * The structural claim of LPN-16, and the one thing that separates this from the tag
     * cloud it replaces. In SheetMark the cloud sat above the list as a *global* control,
     * so the collection had exactly one view at a time. Here the search, the tags, the
     * sort, the grouping, the column count and the density all belong to the **window**,
     * so two windows in one tab can show read-later triage and an untagged sweep side by
     * side, each with its own everything.
     *
     * It was a `data object` for three tickets, and the file header predicted this move
     * and wrote down what makes it safe: **every field defaults**, so a blob written
     * before any of them existed decodes as a window with all of them at their defaults —
     * which is exactly the unfiltered list it used to be. `WorkspaceTest` pins that with
     * a real old blob rather than a re-encoded new one.
     *
     * Two of these being *equal* is not two of these being the *same window* — unlike
     * [Settings] and [EditBookmark], where content identifies a window and a second press
     * focuses the first. "Open two lists side by side" is the thing the workspace exists
     * for, and two lists with identical filters is an ordinary state somebody reaches by
     * pressing "+" twice. So this kind never goes through the singleton path; see
     * `WorkspaceBackingViewModel.onWindowAdded`.
     *
     * **The vocabularies are `String` rather than `enum`**, and the reason is the same one
     * `AiStates` gives on the wire: kotlinx-serialization *refuses* an unknown enum value,
     * so a blob written by a later client that grew a sixth sort would fail to parse — and
     * because [WorkspaceCodec] treats an unreadable blob as no blob at all, the cost of
     * that would be the user's whole arrangement, silently, on one page load. A string it
     * does not recognise instead falls through to a `when`'s `else` and behaves as the
     * default. See [BookmarkSorts] and the objects beside it, which own the values.
     *
     * @property query the free text, searched over title, URL and both summaries. Never a
     *   tag: a word without a `#` is not a label, by `TagInputRules`.
     * @property includeTags tags a bookmark must carry, combined per [match].
     * @property excludeTags tags a bookmark must not carry. **Excluding beats including**,
     *   always — see `filterBookmarks`. The third state of a chip is the useful one in
     *   practice: a tag like `#read-later` covers most of a collection, so the sharpest
     *   cuts come from removing a topic rather than adding one.
     * @property match one of [FilterMatches] — whether [includeTags] are *and* or *or*.
     * @property sort one of [BookmarkSorts].
     * @property group one of [BookmarkGroupings].
     * @property addedWithin one of [AddedWithins] — a rolling window back from now, not a
     *   calendar bucket, so it needs no time zone to be right.
     * @property aiTags one of [AiTagFilters] — whether the model put tags on the row.
     *   Named after the field it reads, `Bookmark.aiTags`, because that is what it asks
     *   about: **the result, not the queue state**. See the note above [AiTagFilters] for
     *   why `Bookmark.aiState` is the wrong thing to look at and what it is for instead.
     * @property aiSummary one of [AiSummaryFilters] — whether the model wrote a summary.
     *   `Bookmark.aiSummary` only; the user's own note is a different field and does not
     *   count. Independent of [aiTags] rather than folded into one *AI* knob, because
     *   "tagged but never summarised" is a real triage state and one select cannot say it.
     * @property columns how many columns of cards, or [BookmarkColumns.AUTO] to pick from
     *   the available width. **A knob, not a separate grid window kind**: the same saved
     *   filter is one column in a narrow split and three when the window is maximised, and
     *   a second kind would mean a second everything to keep in step.
     *
     * A `rowDensity` used to sit beside [columns] and does not any more — see the note where
     * `RowDensities` was, in `BookmarkFilter.kt`. Old blobs still carrying it decode
     * unchanged, because [WorkspaceCodec] ignores keys it does not know.
     */
    @Serializable
    @SerialName("bookmarkList")
    data class BookmarkList(
        val query: String = "",
        val includeTags: List<String> = emptyList(),
        val excludeTags: List<String> = emptyList(),
        val match: String = FilterMatches.ALL,
        val sort: String = BookmarkSorts.NEWEST,
        val group: String = BookmarkGroupings.DAY,
        val addedWithin: String = AddedWithins.ANY,
        val aiTags: String = AiTagFilters.ANY,
        val aiSummary: String = AiSummaryFilters.ANY,
        val columns: Int = BookmarkColumns.AUTO,
    ) : WindowContent

    /**
     * How this LunaPin is configured, and how to get things into it.
     *
     * A window rather than the modal dialog it was, because settings is a surface you
     * consult *while* doing something: a dialog has to be dismissed before you can look
     * at the thing it configures, and it is furniture the workspace cannot remember.
     * This one reopens where it was left, beside the list it explains.
     *
     * Argument-less — the last kind that is — and **at most one may be open**.
     * See [WorkspaceBackingViewModel.onSettingsActivated], which is where the gear's
     * second press becomes a focus rather than a second window, and which has to find
     * that window by *content*: a window with no argument has nothing to derive an id
     * from, which is the whole reason ids are allocated from a counter here (see the
     * file header) and therefore the reason Lunicle's answer to the same question — a
     * pane id derived from what the pane shows — is not available to us.
     */
    @Serializable
    @SerialName("settings")
    data object Settings : WindowContent

    /**
     * One bookmark, open for changing.
     *
     * **The first kind with an argument**, and the file header names the two things that
     * brings with it. Both are settled here rather than left to the surfaces:
     *
     *  - **Two windows of this kind are genuinely different windows.** Content is no
     *    longer enough to identify a window, which is what [Workspace.nextWindowId] was
     *    always for — two bookmarks open side by side are two ids and two entries in the
     *    toolkit's geometry map. What content *is* still enough for is the narrower
     *    question the same bookmark twice asks, and because this is a `data class` that
     *    question is plain equality: see
     *    [WorkspaceBackingViewModel.onEditBookmarkActivated], which focuses the window
     *    that exists rather than opening a second view of one row.
     *  - **The bookmark can be deleted underneath it** — from another window, from a
     *    phone, from the bookmarklet's popup. So an editor has a state that is not a
     *    form: `EditBookmarkBackingViewModel.State.Gone`, reached from the 404 the edit
     *    route answers for an id this account no longer has. Nothing here needs to know
     *    that; a window whose bookmark is gone is still a window, and the id stays in
     *    the blob until somebody closes it.
     *
     * **A window's bookmark never changes.** There is no intent that repoints one, and
     * that is deliberate rather than unfinished: `WindowBodies.contentFor` caches a body
     * by window id and ignores the content afterwards, and a repointable editor is
     * exactly the change that would make that cache wrong. Opening a different bookmark
     * opens a different window.
     *
     * @property bookmarkId the row's own [se.soderbjorn.lunapin.clientserver.Bookmark.id].
     *   Stored rather than the whole bookmark: a blob written weeks ago must reopen onto
     *   what the server holds *now*, and a copy of the title in the workspace would be a
     *   second, staler source of truth about it.
     */
    @Serializable
    @SerialName("editBookmark")
    data class EditBookmark(val bookmarkId: Long) : WindowContent
}

/**
 * One window in a tab.
 *
 * @property id stable identifier, ours, allocated by [Workspace.nextWindowId]. The
 *   toolkit reports it back verbatim on a close, a focus or a sidebar click, and
 *   keys its own persisted geometry by it.
 * @property content what the window shows.
 * @property name what the user called it, or **null for "never named"**.
 *
 *   Null rather than an empty string, and the distinction is the whole point rather than
 *   fussiness: a window that has never been named follows its filter — rename the filter
 *   and the sidebar row changes with it, which is what makes four open windows readable —
 *   while a window that *has* been named keeps that name whatever the filter does. An
 *   empty string is a name somebody typed, and "named, and the name is blank" must stay
 *   tellable apart from "never named", because only the first of them is allowed to stop
 *   following.
 *
 *   In practice a blank commit is how somebody *asks* for the derived name back — the
 *   toolkit's `allowEmptyPaneRename` forwards it rather than treating it as a cancel, and
 *   `WorkspaceBackingViewModel.onWindowRenamed` turns it back into null. So the blank
 *   string never reaches this field; what it does is clear it.
 *
 *   The derived half is `filterSummary`, which is also the footer's sentence — one
 *   function, two call sites, and it lives in `:client` rather than in the browser
 *   precisely so this property has something to fall back to on a platform with no DOM.
 *
 *   A clone copies this field verbatim rather than deriving a name for the copy, which is
 *   what keeps "clone" honest: both windows go on behaving the same way as each other,
 *   whichever of the two states they were in. See
 *   `WorkspaceBackingViewModel.onWindowCloned`.
 */
@Serializable
data class WorkspaceWindow(
    val id: String,
    val content: WindowContent = WindowContent.BookmarkList(),
    val name: String? = null,
)

/**
 * One tab: a working set of windows.
 *
 * @property id stable identifier, ours; the toolkit reports it back verbatim.
 * @property name what the tab strip shows. Renamable, so it is the user's own
 *   sentence rather than a derived one.
 * @property windows what is open in it, in the order they were opened. Where they
 *   *sit* is the toolkit's business (see `UiSettingKeys.LAYOUT_STATE`); this is
 *   only which of them exist.
 * @property activeWindowId the focused window, or null for none.
 */
@Serializable
data class WorkspaceTab(
    val id: String,
    val name: String,
    val windows: List<WorkspaceWindow> = emptyList(),
    val activeWindowId: String? = null,
) {
    /** The window with that id, or null. */
    fun window(windowId: String): WorkspaceWindow? = windows.firstOrNull { it.id == windowId }
}

/**
 * Every tab, and which one is showing.
 *
 * @property tabs ordered. Empty is a real state only before the first restore has
 *   run — see [WorkspaceBackingViewModel], which never leaves it empty afterwards.
 * @property activeTabId a member of [tabs], or null when there are none.
 */
@Serializable
data class Workspace(
    val tabs: List<WorkspaceTab> = emptyList(),
    val activeTabId: String? = null,
) {
    /** The showing tab, or null. */
    val activeTab: WorkspaceTab? get() = tabs.firstOrNull { it.id == activeTabId }

    /** Replace one tab, by id, leaving the rest alone. */
    fun mapTab(tabId: String, transform: (WorkspaceTab) -> WorkspaceTab): Workspace =
        copy(tabs = tabs.map { if (it.id == tabId) transform(it) else it })

    /**
     * An id no tab currently holds, of the form `tab-<n>`.
     *
     * Derived from the ids in use rather than from a clock or a random source, for
     * two reasons. It keeps this whole file a pure function of its input, so
     * [WorkspaceCodec] round-trips are testable without a platform; and shared code
     * has no clock it can reach anyway without an `expect` declaration that would
     * exist solely to name a window.
     *
     * "One past the highest number in use" rather than "one past the count": closing
     * tab-2 of three and adding a tab must not produce `tab-3`, which is still open.
     * A count-based allocator gets exactly that wrong.
     *
     * Note what is *not* claimed: this does not avoid reusing the id of a tab that
     * has been closed. It does not need to — the shell drops a tab's whole geometry
     * entry when the tab leaves the snapshot — and avoiding it would mean storing a
     * high-water mark, i.e. a field in the blob whose only job is to remember
     * something nobody can observe.
     */
    fun nextTabId(): String = nextId(TAB_ID_PREFIX, tabs.map { it.id })

    /**
     * An id no window in the whole workspace currently holds, of the form
     * `window-<n>`.
     *
     * Unique across every tab, not merely within one: the toolkit's geometry map is
     * per-tab, but its focus reporting and its content cache are keyed by window id
     * alone, so two tabs holding a `window-1` each would be one window as far as
     * `paneContent` is concerned. That — collision between two *open* windows — is
     * the whole invariant; see [nextTabId] on why reusing a closed id is deliberately
     * not avoided.
     */
    fun nextWindowId(): String =
        nextId(WINDOW_ID_PREFIX, tabs.flatMap { tab -> tab.windows.map { it.id } })

    companion object {
        const val TAB_ID_PREFIX: String = "tab-"
        const val WINDOW_ID_PREFIX: String = "window-"

        /**
         * `<prefix><one past the highest number already used with that prefix>`.
         *
         * Ids that do not parse as `<prefix><number>` are ignored rather than
         * rejected — a blob written by a future client may spell an id some other
         * way, and the only thing this needs to guarantee is that what it returns
         * is not in [existing].
         */
        private fun nextId(prefix: String, existing: List<String>): String {
            val highest = existing
                .mapNotNull { it.removePrefix(prefix).takeIf { rest -> rest != it }?.toIntOrNull() }
                .maxOrNull()
                ?: 0
            return "$prefix${highest + 1}"
        }
    }
}

/**
 * The `lunapin.workspace.v1` blob, read and written.
 *
 * Wrapped in a versioned envelope rather than serialising [Workspace] directly: the
 * stored shape and the in-memory shape are allowed to diverge, and a `version` that
 * nothing reads yet is what makes the first divergence a migration rather than a
 * loss.
 *
 * Never throws. A blob this code cannot read is treated as no blob at all — the
 * user gets the default workspace, which is a bad day rather than a broken app, and
 * is the only honest response to bytes we do not understand.
 */
object WorkspaceCodec {

    /** The version this client writes. Read but not yet branched on. */
    private const val VERSION = 1

    @Serializable
    private data class Blob(
        val version: Int = VERSION,
        val workspace: Workspace = Workspace(),
    )

    /**
     * `ignoreUnknownKeys` so a newer client's extra fields do not fail an older one;
     * `encodeDefaults` so a tab whose only interesting field is at its default still
     * round-trips as itself; `classDiscriminator = "kind"` so [WindowContent] reads
     * as `{"kind":"bookmarkList"}` rather than with kotlinx's default `type` key, which
     * would collide the day a window gains a field called `type`.
     */
    private val json = Json {
        ignoreUnknownKeys = true
        encodeDefaults = true
        classDiscriminator = "kind"
    }

    /**
     * Parse the stored blob.
     *
     * @return the workspace, or null for absent, unreadable, or empty-of-tabs — all
     *   three of which the caller answers the same way, by seeding a default.
     */
    fun decode(blob: String?): Workspace? {
        val text = blob?.takeIf { it.isNotBlank() } ?: return null
        return runCatching { json.decodeFromString<Blob>(text).workspace }
            .getOrNull()
            ?.takeIf { it.tabs.isNotEmpty() }
    }

    /** Render a workspace to the blob the server stores. */
    fun encode(workspace: Workspace): String = json.encodeToString(Blob(workspace = workspace))
}
